import { useRef, useState } from "react"
import { MaskedCredentialInput } from "@/components/shared/masked-credential-input"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useCreateProVenueInstance } from "@/hooks/use-provenue-instances"
import type { SharedProVenueInstance } from "@/types/provenue-instance"

interface AddProVenueInstanceDialogProps {
  onClose: () => void
  onCreated: (instance: SharedProVenueInstance) => Promise<void>
}

export function AddProVenueInstanceDialog({ onClose, onCreated }: AddProVenueInstanceDialogProps) {
  const createInstance = useCreateProVenueInstance("smithers")
  const submitting = useRef(false)
  const [name, setName] = useState("")
  const [hostName, setHostName] = useState("")
  const [consumerKey, setConsumerKey] = useState("")
  const [consumerSecret, setConsumerSecret] = useState("")
  const [error, setError] = useState<string>()
  let validHost = false
  try {
    const url = new URL(hostName.trim())
    validHost = url.protocol === "https:" && Boolean(url.host)
  } catch { /* The form remains disabled for invalid URLs. */ }
  const hostError = hostName.trim() && !validHost ? "Enter an HTTPS URL, such as https://sandbox.example.com" : undefined
  const valid = Boolean(name.trim() && validHost && consumerKey.trim() && consumerSecret.trim())

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!valid || submitting.current) return
    submitting.current = true
    setError(undefined)
    try {
      const instance = await createInstance.trigger({ name: name.trim(), hostName: hostName.trim(), consumerKey, consumerSecret })
      setConsumerKey("")
      setConsumerSecret("")
      await onCreated(instance)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create instance. Please try again.")
    } finally {
      submitting.current = false
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !submitting.current) onClose() }}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add ProVenue Instance</DialogTitle>
          <DialogDescription>Enter the connection details and credentials for a shared ProVenue instance.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {error && <Alert variant="destructive"><AlertTitle>Couldn’t create instance</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
          <div className="flex flex-col gap-2">
            <Label htmlFor="provenue-name">Name</Label>
            <Input id="provenue-name" value={name} onChange={(event) => setName(event.target.value)} required disabled={createInstance.isMutating} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="provenue-host">Host Name</Label>
            <Input id="provenue-host" value={hostName} onChange={(event) => setHostName(event.target.value)} placeholder="https://sandbox.example.com" required disabled={createInstance.isMutating} aria-invalid={Boolean(hostError)} aria-describedby={hostError ? "provenue-host-error" : undefined} />
            {hostError && <p id="provenue-host-error" className="text-sm text-destructive" role="alert">{hostError}</p>}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="provenue-key">Consumer Key</Label>
            <MaskedCredentialInput id="provenue-key" value={consumerKey} onChange={(event) => setConsumerKey(event.target.value)} required disabled={createInstance.isMutating} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="provenue-secret">Consumer Secret</Label>
            <MaskedCredentialInput id="provenue-secret" value={consumerSecret} onChange={(event) => setConsumerSecret(event.target.value)} required disabled={createInstance.isMutating} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={createInstance.isMutating}>Cancel</Button>
            <Button type="submit" disabled={!valid || createInstance.isMutating}>{createInstance.isMutating ? "Creating…" : "Create Instance"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
